import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../contexts/AuthContext';
import { paymentService } from '../services/paymentService';
import toast from 'react-hot-toast';
import { useSEO } from '../hooks/useSEO';

const AccountPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: ''
  });

  const { user, userProfile, logout, updateProfile, deleteUserAccount } = useAuth();
  const navigate = useNavigate();
  
  useSEO({
    title: `My Account - ${userProfile?.firstName || 'User'} | AM CARDMATE`,
    description: "Manage your AM CARDMATE account, view orders, and update profile.",
    keywords: "user account, profile management"
  });

  useEffect(() => {
    if (user && userProfile) {
      const schema = {
        "@context": "https://schema.org",
        "@type": "ProfilePage",
        "name": `${userProfile?.firstName || 'User'}'s Account`,
        "description": "Manage your AM CARDMATE account",
        "url": "https://amcardmate.com/account"
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }
  }, [user, userProfile]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userProfile) {
      setProfileData({
        firstName: userProfile.firstName || '',
        lastName: userProfile.lastName || '',
        email: userProfile.email || user.email || '',
        phone: userProfile.phone || '',
        company: userProfile.company || ''
      });
    }
  }, [user, userProfile, navigate]);

  useEffect(() => {
    if (activeTab === 'orders') {
      loadOrders();
    }
  }, [activeTab, user]);

  const loadOrders = useCallback(async () => {
    if (!user || loadingOrders) return;

    setLoadingOrders(true);
    try {
      const { data, error } = await paymentService.getUserOrders(user.uid);
      if (!error && data) {
        const uniqueOrders = data.filter((order, index, self) => {
          return index === self.findIndex(o => 
            (o.paymentIntentId && o.paymentIntentId === order.paymentIntentId) ||
            (o.orderNumber && o.orderNumber === order.orderNumber) ||
            o.id === order.id
          );
        });
        
        uniqueOrders.sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
          return dateB - dateA;
        });
        
        setOrders(uniqueOrders);
      }
    } catch (error) {}
    setLoadingOrders(false);
  }, [user, loadingOrders]);

  const handleCancelOrder = useCallback(async (orderId) => {
    if (cancelling === orderId) return;

    try {
      setCancelling(orderId);
      const { success, error } = await paymentService.cancelOrder(orderId, 'Customer requested cancellation');
      if (success) {
        loadOrders();
      } else {
        toast.error(error || 'Failed to cancel order');
      }
    } catch (error) {
      toast.error('Failed to cancel order');
    } finally {
      setCancelling(null);
      setShowCancelModal(false);
      setOrderToCancel(null);
    }
  }, [cancelling, loadOrders]);

  const handleProfileUpdate = useCallback(async (e) => {
    e.preventDefault();
    if (updating) return;
    setUpdating(true);
    
    try {
      const result = await updateProfile(profileData);
      if (!result.error) {
        setEditMode(false);
      } else {
        toast.error(result.error || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setUpdating(false);
    }
  }, [updating, updateProfile, profileData]);

  const handleLogout = useCallback(async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to logout');
    }
  }, [logout, navigate]);

  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirmText !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (deletingAccount) return;

    setDeletingAccount(true);
    try {
      const result = await deleteUserAccount();
      if (!result.error) {
        toast.success('Account deleted successfully');
        navigate('/');
      } else {
        toast.error(result.error || 'Failed to delete account');
      }
    } catch (error) {
      toast.error('Failed to delete account');
    } finally {
      setDeletingAccount(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  }, [deleteConfirmText, deleteUserAccount, navigate, deletingAccount]);

  const formatDate = useCallback((timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'pending': return 'text-yellow-800 bg-yellow-100 border-yellow-200';
      case 'processing': return 'text-blue-800 bg-blue-100 border-blue-200';
      case 'shipped': return 'text-purple-800 bg-purple-100 border-purple-200';
      case 'delivered': return 'text-green-800 bg-green-100 border-green-200';
      case 'cancelled': return 'text-red-800 bg-red-100 border-red-200';
      default: return 'text-gray-800 bg-gray-100 border-gray-200';
    }
  }, []);

  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'pending': return 'hourglass-half';
      case 'processing': return 'cog';
      case 'shipped': return 'truck';
      case 'delivered': return 'check-circle';
      case 'cancelled': return 'times-circle';
      default: return 'question-circle';
    }
  }, []);

  const calculateOrderTotal = useCallback((order) => {
    const subtotal = order.total || 0;
    const shipping = order.shippingCost || 0;
    return subtotal + shipping;
  }, []);

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <FontAwesomeIcon icon="spinner" className="text-4xl text-blue-500 animate-spin mb-4" />
          <p className="text-body text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 sm:py-12 px-4 sm:px-6">
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-200/20 to-purple-200/20 rounded-full filter blur-3xl" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-purple-200/20 to-pink-200/20 rounded-full filter blur-3xl" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-h4 font-bold text-white">
              {userProfile?.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
            </span>
          </div>
          <h1 className="text-h3 text-dark mb-2">
            Welcome back, <span className="text-gradient-blue-pink">{userProfile?.firstName || 'User'}</span>
          </h1>
          <p className="text-body text-gray-600">Manage your account and orders</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-4 sm:p-6">
              <nav className="space-y-2">
                {[
                  { id: 'profile', icon: 'user', label: 'Profile' },
                  { id: 'orders', icon: 'shopping-bag', label: `Orders ${orders.length > 0 ? `(${orders.length})` : ''}` },
                  { id: 'settings', icon: 'cog', label: 'Settings' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full text-left px-4 py-3 rounded-2xl transition-all duration-300 ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg transform scale-105'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    <FontAwesomeIcon icon={tab.icon} className="mr-3" />
                    <span className="text-body-sm">{tab.label}</span>
                  </button>
                ))}
                
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-2xl text-red-600 hover:bg-red-50 transition-all duration-300"
                >
                  <FontAwesomeIcon icon="sign-out-alt" className="mr-3" />
                  <span className="text-body-sm">Logout</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                  <h2 className="text-h4 text-dark mb-4 sm:mb-0">Profile Information</h2>
                  <button
                    onClick={() => setEditMode(!editMode)}
                    disabled={updating}
                    className={`btn ${editMode ? 'btn-outline text-gray-600' : 'btn-outline'} disabled:opacity-50`}
                  >
                    <FontAwesomeIcon icon={editMode ? "times" : "edit"} />
                    <span>{editMode ? 'Cancel' : 'Edit'}</span>
                  </button>
                </div>

                <form onSubmit={handleProfileUpdate}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { key: 'firstName', label: 'First Name', type: 'text', editable: true },
                      { key: 'lastName', label: 'Last Name', type: 'text', editable: true },
                      { key: 'email', label: 'Email', type: 'email', editable: false, hint: 'Email cannot be changed' },
                      { key: 'phone', label: 'Phone', type: 'tel', editable: true },
                      { key: 'company', label: 'Company', type: 'text', editable: true, fullWidth: true }
                    ].map((field) => (
                      <div key={field.key} className={field.fullWidth ? 'sm:col-span-2' : ''}>
                        <label className="block text-label text-gray-700 mb-2">{field.label}</label>
                        <input
                          type={field.type}
                          value={profileData[field.key]}
                          onChange={(e) => setProfileData({...profileData, [field.key]: e.target.value})}
                          disabled={!field.editable || !editMode || updating}
                          className={`w-full px-4 py-3 rounded-2xl text-body transition-all duration-300 ${
                            editMode && field.editable && !updating
                              ? 'bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white shadow-sm' 
                              : 'bg-gray-50/70'
                          }`}
                        />
                        {field.hint && <p className="text-caption mt-1">{field.hint}</p>}
                      </div>
                    ))}
                  </div>

                  {editMode && (
                    <div className="mt-6">
                      <button type="submit" disabled={updating} className="btn btn-primary disabled:opacity-50">
                        <FontAwesomeIcon icon={updating ? "spinner" : "save"} className={updating ? "animate-spin" : ""} />
                        <span>{updating ? 'Saving...' : 'Save Changes'}</span>
                      </button>
                    </div>
                  )}
                </form>
              </div>
            )}

            {/* Orders Tab */}
            {activeTab === 'orders' && (
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-h4 text-dark">Order History</h2>
                  {orders.length > 0 && (
                    <button onClick={loadOrders} className="btn btn-outline btn-sm" disabled={loadingOrders}>
                      <FontAwesomeIcon icon={loadingOrders ? "spinner" : "refresh"} className={loadingOrders ? "animate-spin" : ""} />
                      <span>Refresh</span>
                    </button>
                  )}
                </div>

                {loadingOrders ? (
                  <div className="text-center py-12">
                    <FontAwesomeIcon icon="spinner" className="text-4xl text-blue-500 animate-spin mb-4" />
                    <p className="text-body text-gray-600">Loading your orders...</p>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                      <FontAwesomeIcon icon="shopping-bag" className="text-3xl text-gray-400" />
                    </div>
                    <h3 className="text-h5 text-dark mb-2">No orders yet</h3>
                    <p className="text-body text-gray-600 mb-6">You haven't placed any orders yet. Start shopping!</p>
                    <button onClick={() => navigate('/nfc-cards')} className="btn btn-primary">
                      <FontAwesomeIcon icon="credit-card" />
                      <span>Shop NFC Cards</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {orders.map((order) => (
                      <div key={order.id} className="bg-white/50 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                          <div>
                            <h3 className="text-card-title text-dark mb-1">
                              Order #{order.orderNumber || order.id.slice(-8).toUpperCase()}
                            </h3>
                            <p className="text-body-sm text-gray-600">Placed on {formatDate(order.createdAt)}</p>
                          </div>
                          <div className="flex items-center gap-3 mt-3 sm:mt-0">
                            <span className={`px-3 py-1 rounded-full text-body-sm font-medium border ${getStatusColor(order.status)}`}>
                              <FontAwesomeIcon icon={getStatusIcon(order.status)} className="mr-1" />
                              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            </span>
                            {order.status === 'processing' && (
                              <button
                                onClick={() => { setOrderToCancel(order.id); setShowCancelModal(true); }}
                                disabled={cancelling === order.id}
                                className="text-red-500 hover:text-red-700 text-body-sm font-medium px-2 py-1 hover:bg-red-50 rounded transition-all disabled:opacity-50"
                              >
                                {cancelling === order.id ? <FontAwesomeIcon icon="spinner" className="animate-spin" /> : 'Cancel'}
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="mb-6">
                          <h4 className="text-body font-semibold text-dark mb-3">Items Ordered</h4>
                          <div className="space-y-3">
                            {order.items?.map((item, index) => (
                              <div key={index} className="flex items-center gap-4 p-3 bg-white/70 rounded-xl">
                                <div className="w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center">
                                  {item.image ? (
                                    <img src={item.image} alt={item.name} className="w-full h-full object-contain rounded-xl" />
                                  ) : (
                                    <FontAwesomeIcon icon="credit-card" className="text-gray-400" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-body font-medium text-dark">{item.name}</p>
                                  <p className="text-body-sm text-gray-600">Quantity: {item.quantity}</p>
                                </div>
                                <p className="text-body font-bold">£{(item.price * item.quantity).toFixed(2)}</p>
                              </div>
                            ))}
                          </div>
                        </div>

                        {order.shippingInfo && (
                          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-body font-semibold text-dark mb-2">Shipping Address</h4>
                              <div className="text-body-sm text-gray-600 space-y-1">
                                <p className="font-medium text-dark">{order.shippingInfo.firstName} {order.shippingInfo.lastName}</p>
                                <p>{order.shippingInfo.address}</p>
                                <p>{order.shippingInfo.city}, {order.shippingInfo.postalCode}</p>
                                <p>{order.shippingInfo.country}</p>
                              </div>
                            </div>
                            {order.trackingNumber && (
                              <div>
                                <h4 className="text-body font-semibold text-dark mb-2">Tracking</h4>
                                <div className="bg-blue-50 rounded-xl p-3">
                                  <p className="text-body-sm">
                                    <span className="font-medium text-blue-800">Tracking Number: </span>
                                    <span className="text-blue-600 font-mono">{order.trackingNumber}</span>
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        <div className="border-t border-gray-200 pt-4">
                          <div className="flex justify-between items-center mb-2 text-body-sm">
                            <span className="text-gray-600">Subtotal:</span>
                            <span>£{order.total?.toFixed(2)}</span>
                          </div>
                          {order.shippingCost !== undefined && (
                            <div className="flex justify-between items-center mb-2 text-body-sm">
                              <span className="text-gray-600">Shipping:</span>
                              <span className={order.shippingCost === 0 ? "text-green-600 font-medium" : ""}>
                                {order.shippingCost === 0 ? 'FREE ✨' : `£${order.shippingCost.toFixed(2)}`}
                              </span>
                            </div>
                          )}
                          <div className="flex justify-between items-center text-body-lg font-bold border-t border-gray-200 pt-2">
                            <span>Total Paid:</span>
                            <span className="text-gradient-blue-pink">£{calculateOrderTotal(order).toFixed(2)}</span>
                          </div>
                        </div>

                        {order.paymentIntentId && order.paymentStatus === 'completed' && (
                          <div className="mt-4 p-3 bg-green-50 rounded-xl">
                            <div className="flex items-center gap-2">
                              <FontAwesomeIcon icon="check-circle" className="text-green-600" />
                              <span className="text-body-sm font-medium text-green-800">Payment Confirmed</span>
                            </div>
                            <p className="text-caption text-green-600 mt-1">Payment ID: {order.paymentIntentId}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-xl p-6 sm:p-8">
                <h2 className="text-h4 text-dark mb-6">Account Settings</h2>

                <div className="space-y-6">
                  <div className="bg-white/50 rounded-2xl p-6">
                    <h3 className="text-h5 text-dark mb-4">Account Information</h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Email', value: user.email },
                        { label: 'Email Verified', value: user.emailVerified ? 'Verified' : 'Not Verified', color: user.emailVerified ? 'text-green-600' : 'text-orange-600' },
                        { label: 'Account created', value: user.metadata?.creationTime ? formatDate(user.metadata.creationTime) : 'N/A' },
                        { label: 'Last sign in', value: user.metadata?.lastSignInTime ? formatDate(user.metadata.lastSignInTime) : 'N/A' },
                        { label: 'Total Orders', value: orders.length }
                      ].map((item, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="text-body text-gray-600">{item.label}:</span>
                          <span className={`text-body font-medium ${item.color || ''}`}>{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/50 rounded-2xl p-6">
                    <h3 className="text-h5 text-dark mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { icon: 'credit-card', label: 'Shop NFC Cards', color: 'text-purple-500', action: () => navigate('/nfc-cards') },
                        { icon: 'headset', label: 'Contact Support', color: 'text-green-500', action: () => navigate('/contact') }
                      ].map((item, i) => (
                        <button key={i} onClick={item.action} className="flex items-center justify-between p-3 rounded-xl hover:bg-white/70 transition-all duration-300 group">
                          <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={item.icon} className={item.color} />
                            <span className="text-body">{item.label}</span>
                          </div>
                          <FontAwesomeIcon icon="chevron-right" className="text-gray-400 group-hover:translate-x-1 transition-transform" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white/50 rounded-2xl p-6">
                    <h3 className="text-h5 text-dark mb-4">Security & Privacy</h3>
                    <div className="space-y-3">
                      {[
                        { icon: 'key', label: 'Change Password', color: 'text-yellow-500', action: () => navigate('/change-password') },
                        { icon: 'shield-alt', label: 'Privacy Settings', color: 'text-blue-500', action: () => setShowPrivacyModal(true) }
                      ].map((item, i) => (
                        <button key={i} onClick={item.action} className="w-full text-left flex items-center justify-between p-3 rounded-xl hover:bg-white/70 transition-all duration-300 group">
                          <div className="flex items-center gap-3">
                            <FontAwesomeIcon icon={item.icon} className={item.color} />
                            <span className="text-body">{item.label}</span>
                          </div>
                          <FontAwesomeIcon icon="chevron-right" className="text-gray-400 group-hover:translate-x-1 transition-transform" />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-red-50/70 backdrop-blur-sm rounded-2xl p-6 border border-red-200">
                    <h3 className="text-h5 text-red-600 mb-4">Danger Zone</h3>
                    <div className="space-y-3">
                      <button onClick={handleLogout} className="w-full btn bg-red-100 text-red-600 hover:bg-red-200">
                        <FontAwesomeIcon icon="sign-out-alt" />
                        <span>Sign Out</span>
                      </button>
                      <button onClick={() => setShowDeleteModal(true)} className="w-full btn bg-red-600 text-white hover:bg-red-700">
                        <FontAwesomeIcon icon="trash" />
                        <span>Delete Account</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-2xl text-orange-600" />
              </div>
              <h3 className="text-h5 text-dark mb-2">Cancel Order</h3>
              <p className="text-body-sm text-gray-600">Are you sure you want to cancel this order? This action cannot be undone.</p>
            </div>
            <div className="p-4 bg-orange-50 rounded-2xl border border-orange-200 mb-6">
              <h4 className="text-body font-semibold text-orange-800 mb-2">What happens next:</h4>
              <ul className="text-body-sm text-orange-700 space-y-1">
                <li>• Your order will be cancelled immediately</li>
                <li>• Any payment will be refunded within 3-5 business days</li>
                <li>• You'll receive a confirmation email</li>
              </ul>
            </div>
            <div className="flex gap-3">
              <button onClick={() => handleCancelOrder(orderToCancel)} disabled={cancelling === orderToCancel} className="flex-1 btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                <FontAwesomeIcon icon={cancelling === orderToCancel ? "spinner" : "times"} className={cancelling === orderToCancel ? "animate-spin" : ""} />
                <span>{cancelling === orderToCancel ? 'Cancelling...' : 'Yes, Cancel Order'}</span>
              </button>
              <button onClick={() => { setShowCancelModal(false); setOrderToCancel(null); }} className="flex-1 btn btn-outline">Keep Order</button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-h5 text-dark">Privacy Settings</h3>
              <button onClick={() => setShowPrivacyModal(false)} className="text-gray-500 hover:text-gray-700 p-2 rounded-xl hover:bg-gray-100">
                <FontAwesomeIcon icon="times" />
              </button>
            </div>
            <div className="space-y-4">
              {[
                { title: 'Data Collection', desc: 'We collect only essential data to provide our services.', label: 'Analytics Tracking', bg: 'bg-blue-50', color: 'text-blue-800', descColor: 'text-blue-700', toggle: true, defaultChecked: true },
                { title: 'Marketing Communications', desc: 'Receive updates about new products and offers.', label: 'Email Marketing', bg: 'bg-green-50', color: 'text-green-800', descColor: 'text-green-700', toggle: true },
                { title: 'Data Sharing', desc: 'We never share your personal data with third parties for marketing.', label: 'Essential Services Only', bg: 'bg-yellow-50', color: 'text-yellow-800', descColor: 'text-yellow-700', checkmark: true }
              ].map((item, i) => (
                <div key={i} className={`p-4 ${item.bg} rounded-2xl`}>
                  <h4 className={`text-body font-semibold ${item.color} mb-2`}>{item.title}</h4>
                  <p className={`text-body-sm ${item.descColor} mb-3`}>{item.desc}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-body-sm">{item.label}</span>
                    {item.toggle ? (
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" className="sr-only peer" defaultChecked={item.defaultChecked} />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    ) : (
                      <FontAwesomeIcon icon="check-circle" className="text-green-600" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button onClick={() => { toast.success('Privacy settings updated'); setShowPrivacyModal(false); }} className="flex-1 btn btn-primary">Save Settings</button>
              <button onClick={() => setShowPrivacyModal(false)} className="flex-1 btn btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 max-w-md w-full shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon="exclamation-triangle" className="text-2xl text-red-600" />
              </div>
              <h3 className="text-h5 text-dark mb-2">Delete Account</h3>
              <p className="text-body-sm text-gray-600">This action cannot be undone. All your data will be permanently deleted.</p>
            </div>
            <div className="p-4 bg-red-50 rounded-2xl border border-red-200 mb-4">
              <h4 className="text-body font-semibold text-red-800 mb-2">What will be deleted:</h4>
              <ul className="text-body-sm text-red-700 space-y-1">
                <li>• Your profile and account information</li>
                <li>• All order history and data</li>
                <li>• Saved preferences and settings</li>
                <li>• All associated data</li>
              </ul>
            </div>
            <div className="mb-4">
              <label className="block text-label text-gray-700 mb-2">Type "DELETE MY ACCOUNT" to confirm:</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-2xl text-body focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="DELETE MY ACCOUNT"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={handleDeleteAccount} disabled={deletingAccount || deleteConfirmText !== 'DELETE MY ACCOUNT'} className="flex-1 btn bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                <FontAwesomeIcon icon={deletingAccount ? "spinner" : "trash"} className={deletingAccount ? "animate-spin" : ""} />
                <span>{deletingAccount ? 'Deleting...' : 'Delete Account'}</span>
              </button>
              <button onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }} className="flex-1 btn btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountPage;